import React, { useState, useMemo, useEffect } from 'react';
import { StyleSheet, Pressable, View, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import * as ImagePicker from 'expo-image-picker';
import { Image } from 'expo-image';
import Button from '@/components/Buttons/Button';
import SelectInput from '@/components/Inputs/SelectInput';
import { useAuthStore, useToastStore } from '@/store';
import { useTranslation } from '@/hooks/useTranslation';
import SettingsSection from '@/components/SettingsSection';
import FloatingLabelInput from '@/components/Inputs/FloatingLabelInput';
import { FILE_SIZE_5MB } from '@/constants/config';

const createStyles = (colors) => StyleSheet.create({
  section: {
    marginBottom: 20,
  },
  avatarContainer: {
    width: 80,
    height: 80,
    borderRadius: 50,
    backgroundColor: colors.solid_color_secondary,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    overflow: 'hidden',
  },
  avatarImage: {
    width: Platform.OS === 'macos' ? 80 : '100%',
    height: Platform.OS === 'macos' ? 80 : '100%',
  },
  avatarSection: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 16,
  },
  avatarControls: {
    flex: 1,
    justifyContent: 'center',
  },
  avatarButtons: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    alignItems: 'center',
  },
  avatarHelperText: {
    marginTop: 6,
  },
  error: {
    marginTop: 8,
    color: colors.color_destructive,
  },
  subtitle: {
    marginTop: -4,
    marginBottom: 12,
    lineHeight: 18,
  },
  saveButton: {
    marginTop: 20,
    width: '100%',
  },
});

function ProfileSettings({ onSetHeaderActions, onDirtyChange }) {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  
  const userFullName = useAuthStore(state => state.user?.fullName);
  const userAvatarPath = useAuthStore(state => state.user?.avatarPath);
  const userProfessionalRole = useAuthStore(state => state.user?.professionalRole);
  const isPlus = useAuthStore(state => state.user?.flags?.isPlus);
  const updateProfile = useAuthStore(state => state.updateProfile);
  const fetchUser = useAuthStore(state => state.fetchUser);

  const { addToast } = useToastStore(state => state.actions);
  const { t } = useTranslation();

  const ROLE_OPTIONS = useMemo(() => [
    { label: t('settings.profileRoles.product_management'), value: 'product_management' },
    { label: t('settings.profileRoles.engineering'), value: 'engineering' },
    { label: t('settings.profileRoles.human_resources'), value: 'human_resources' },
    { label: t('settings.profileRoles.finance'), value: 'finance' },
    { label: t('settings.profileRoles.marketing'), value: 'marketing' },
    { label: t('settings.profileRoles.sales'), value: 'sales' },
    { label: t('settings.profileRoles.operations'), value: 'operations' },
    { label: t('settings.profileRoles.data_science'), value: 'data_science' },
    { label: t('settings.profileRoles.design'), value: 'design' },
    { label: t('settings.profileRoles.legal'), value: 'legal' },
    { label: t('settings.profileRoles.other'), value: 'other' },
  ], [t]);

  const [fullName, setFullName] = useState(userFullName || '');
  const [avatar, setAvatar] = useState(userAvatarPath ? { uri: userAvatarPath } : null);
  const [role, setRole] = useState(userProfessionalRole || 'other');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const isDirty = fullName.trim() !== (userFullName || '')
    || role !== (userProfessionalRole || 'other')
    || (avatar?.uri || null) !== (userAvatarPath || null);
  useEffect(() => { onDirtyChange?.(isDirty); }, [isDirty, onDirtyChange]);

  const handleImagePick = async () => {
    try {
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        aspect: [1, 1],
        quality: 0.7,
        allowsMultipleSelection: false,
      });

      if (!result.canceled && result.assets && result.assets.length > 0) {
        const selectedImage = result.assets[0];
        
        if (selectedImage.fileSize && selectedImage.fileSize > FILE_SIZE_5MB) {
          setError(t('settings.profile.fileSizeLimit'));
          return;
        }

        const mimeToExt = { 'image/png': 'png', 'image/jpeg': 'jpg', 'image/gif': 'gif', 'image/webp': 'webp' };
        const extToMime = { png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif', webp: 'image/webp' };

        let detectedMime = null;
        let detectedExt = null;

        if (selectedImage.mimeType && mimeToExt[selectedImage.mimeType]) {
          detectedMime = selectedImage.mimeType;
          detectedExt = mimeToExt[detectedMime];
        } else {
          const uriExt = selectedImage.uri.split('.').pop()?.split('?')[0]?.toLowerCase();
          if (uriExt && extToMime[uriExt]) {
            detectedExt = uriExt === 'jpeg' ? 'jpg' : uriExt;
            detectedMime = extToMime[uriExt];
          }
        }

        if (!detectedMime) {
          setError('Please select a PNG, JPG, GIF, or WebP image.');
          return;
        }

        const newAvatar = {
          uri: selectedImage.uri,
          type: detectedMime,
          name: `avatar.${detectedExt}`,
        };

        setAvatar(newAvatar);
        setError('');
      }
    } catch (err) {
      console.error('Error picking image:', err);
      setError(t('settings.profile.failedToPickImage'));
    }
  };

  const handleSave = async () => {
    try {
      setIsLoading(true);
      setError('');

      // Prepare the data object
      const data = {
        fullName: fullName.trim(),
        professionalRole: role,
      };

      // Handle avatar changes
      if (avatar && avatar.uri !== userAvatarPath) {
        // Only include avatar if it's new or changed
        data.avatar = avatar;
      } else if (!avatar && userAvatarPath) {
        // If avatar was removed
        data.removeAvatar = true;
      }

      // If no changes were made, show message and return
      if (
        fullName.trim() === (userFullName || '') &&
        role === (userProfessionalRole || 'other') &&
        !data.avatar &&
        !data.removeAvatar
      ) {
        addToast(t('settings.profile.noChanges'), 'info');
        return;
      }

      const result = await updateProfile(data);

      if (result.success) {
        addToast(t('settings.profile.profileUpdated'), 'success');
        const fetchResult = await fetchUser().catch(() => null);
        if (fetchResult?.avatarPath) {
          setAvatar({ uri: fetchResult.avatarPath });
        } else if (data.removeAvatar) {
          setAvatar(null);
        }
      } else {
        throw new Error(result.error?.message || t('settings.profile.profileUpdateFailed'));
      }
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(err.message || t('settings.profile.profileUpdateFailed'));
      addToast(err.message || t('settings.profile.profileUpdateFailed'), 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const hasHeaderActions = typeof onSetHeaderActions === 'function';

  useEffect(() => {
    if (!hasHeaderActions) return;
    onSetHeaderActions([{
      label: t('settings.profile.saveChanges'),
      onPress: handleSave,
      isLoading,
      isDisabled: isLoading || !isDirty,
      variant: 'primary',
    }]);
    return () => onSetHeaderActions([]);
  }, [fullName, avatar, role, isLoading, isDirty, userAvatarPath, userFullName, userProfessionalRole, t, onSetHeaderActions]);

  return (
    <View>
      {isPlus && (
        <SettingsSection
          title={t('settings.profile.profilePhoto')} 
          subtitle={t('settings.profile.profilePhotoSubtitle')}
          isBold
          isColumn
          isFirst
        >
          <View style={styles.avatarSection}>
            <Pressable 
              accessibilityRole="button"
              accessibilityLabel={t('settings.profile.changePhoto')}
              onPress={handleImagePick}
              style={({ hovered }) => [
                styles.avatarContainer,
                hovered && { borderColor: colors.accent_color_primary }
              ]}
            >
              {avatar ? (
                <Image 
                  source={{ uri: avatar.uri }} 
                  style={styles.avatarImage} 
                  contentFit="cover"
                  onError={() => setError(t('settings.profile.failedToLoadImage'))}
                />
              ) : (
                <Icon name="user" size={40} color={colors.text_color_secondary} />
              )}
            </Pressable>
            <View style={styles.avatarControls}>
              <View style={styles.avatarButtons}>
                <Button
                  variant="secondary"
                  size="small"
                  prependIcon="image"
                  text={t('settings.profile.changePhoto')}
                  onPress={handleImagePick}
                />
                {avatar && (
                  <Button
                    variant="secondary"
                    size="small"
                    prependIcon="trash"
                    text={t('settings.profile.remove')}
                    onPress={() => setAvatar(null)}
                  />
                )}
              </View>
              <Text color='tertiary' size='xs' style={styles.avatarHelperText}>
                PNG, JPG, GIF, or WebP — max 5MB
              </Text>

            </View>
          </View>
        </SettingsSection>
      )}

      <SettingsSection
        title={t('settings.profile.fullName')}
        subtitle={t('settings.profile.fullNameSubtitle')}
        isBold
        isColumn
        isFirst={!isPlus}
      >
        <FloatingLabelInput
          label={t('settings.profile.fullName')}
          value={fullName}
          onChangeText={setFullName}
          isFullWidth
        />
      </SettingsSection>

      <SettingsSection
          title={t('settings.profile.professionalRole')}
          subtitle={t('settings.profile.professionalRoleSubtitle')}
          isBold
          isColumn
          isLast
        >
          <SelectInput
            value={role}
            onValueChange={setRole}
            items={ROLE_OPTIONS}
          />
        </SettingsSection>

      {!!error && <Text accessibilityRole="alert" style={styles.error}>{error}</Text>}

      {!hasHeaderActions && (
        <Button
          text={t('settings.profile.saveChanges')}
          variant="primary"
          isLoading={isLoading}
          isDisabled={isLoading || !isDirty}
          onPress={handleSave}
          style={styles.saveButton}
        />
      )}

    </View>
  );
}

export default React.memo(ProfileSettings);
